import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { DownloadSimple, Gift, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AiConsentBox, AiHowDialog } from "@/components/AiConsent";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadPdf } from "@/lib/download";
import { PRICING } from "@/data/categories";

const PRICE = PRICING.ai.find((a) => a.key === "memory_insight")?.price;

export const MemoryInsightPanel = ({ event, access, onSaved }) => {
  const navigate = useNavigate();
  const [consent, setConsent] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const insight = event.memory_insight;

  const write = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/events/${event.id}/insight`, { consent: true });
      onSaved(r.data.memory_insight);
      toast.success("Your Memory Insight is saved to your account.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="memory-insight-panel" className="rounded-xl border border-[#B08D57]/30 bg-[#FBF4E8] p-5">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#A84A32]">
        <Sparkle size={14} weight="fill" /> Memory Insight
      </p>
      {insight?.text ? (
        <>
          <div data-testid="memory-insight-text" className="mt-3 space-y-3 font-serif text-[17px] leading-relaxed text-[#2C2A28]">
            {insight.text.split(/\n+/).map((p, i) => <p key={i}>{p}</p>)}
          </div>
          <Button data-testid="memory-insight-download" variant="outline" onClick={() => downloadPdf(`/events/${event.id}/insight/pdf`, `Memory-Insight-${event.title}`)}
            className="mt-4 rounded-full border-[#A84A32] text-[#A84A32] hover:bg-[#A84A32] hover:text-white">
            <DownloadSimple size={16} className="mr-1.5" /> Download insight PDF
          </Button>
        </>
      ) : access ? (
        <div className="mt-3 space-y-3">
          <p className="text-sm text-[#4A4540]">
            {access === "gift"
              ? <><Gift size={15} weight="duotone" className="mr-1 inline text-[#A84A32]" />Your complimentary Memory Insight — our gift for writing five Childhood memories.</>
              : "Your Memory Insight for this memory is ready to write. It's yours to reread and download anytime, at no extra cost."}
          </p>
          <AiConsentBox k="memory_insight" checked={consent} onChange={setConsent} onHow={() => setHowOpen(true)} />
          <Button data-testid="memory-insight-write" disabled={!consent || busy} onClick={write}
            className="rounded-full bg-[#A84A32] text-white hover:bg-[#8f3e29]">
            <Sparkle size={16} weight="duotone" className="mr-1.5" /> {busy ? "Writing your insight…" : "Write my Memory Insight"}
          </Button>
        </div>
      ) : (
        <div className="mt-3">
          <p className="text-sm leading-relaxed text-[#4A4540]">
            A short, kind and meaningful insight of up to 300 words, written only from this memory. Saved to your account with a PDF download.
          </p>
          <Button data-testid="memory-insight-buy" onClick={() => navigate(`/memory-insight/${event.id}`)}
            className="mt-3 rounded-full bg-[#A84A32] text-white hover:bg-[#8f3e29]">
            Get Memory Insight · {PRICING.currency}{PRICE?.toFixed(2)}
          </Button>
          <p className="mt-1.5 text-xs text-[#8A7F76]">AUD · No GST applies · one-time · pay by card or PayPal</p>
        </div>
      )}
      <AiHowDialog open={howOpen} onOpenChange={setHowOpen} />
    </div>
  );
};
